import { Scene, PerspectiveCamera } from 'three';
import { createRenderer } from './renderer';
import { handleResize } from './resize';
import { poseFromCameraPose } from './cameraRig';
import {
  createDownloadState,
  advanceDownload,
  downloadFraction,
  buildBarFraction,
  isPaintDue,
  COMPILE_START_FRACTION,
} from './loadProgress';
import { QUALITY_TIERS } from '../render/quality';
import { createMaterialFactory } from '../render/materials/materialFactory';
import { createPostPipeline } from '../render/post/postPipeline';
import { createSkyDome } from '../render/sky';
import { createStyleParams } from '../render/style';
import { loadAppData, type DataUrls } from '../data/loadData';
import { createProgressReporter, buildWorld, getTotalBuildWeight } from '../world/buildWorld';
import { afterNextPaint } from './paintYield';
import { deferBoatWater } from '../nav/boatWater';
import { buildBoatWater } from '../world/boatWorld';
import { CAMERA, WORLD } from '../core/config';
import { isOpenWater, waveHeight } from '../render/waves';
import { loadCitizens } from '../world/life/citizens/loadCitizens';
import { WATER_KIND_CODE } from '../world/mesh/attributes';
import { sceneSeconds } from './sceneClock';
import type { ControlEnv } from '../controls/types';
import type { QualityTier } from '../render/types';
import type { AppData } from '../data/types';
import type { ErrorModal, LoadingScreen } from '../ui/types';
import type { UrlOptions } from './types';

import layoutUrl from '../data/amsterdam1538.layout.json?url';
import paletteUrl from '../data/palette.json?url';
import painterViewUrl from '../data/painterView.json?url';
import citizensUrl from '../data/citizens.bin?url';

export type WorldInit = NonNullable<Awaited<ReturnType<typeof initializeWorld>>>;

export interface StartupScreens {
  readonly loadingScreen: LoadingScreen;
  readonly errorModal: ErrorModal;
}

const DATA_URLS: DataUrls = { layout: layoutUrl, palette: paletteUrl, painterView: painterViewUrl };

export async function initializeWorld(
  win: Window,
  screens: StartupScreens,
  options: Pick<UrlOptions, 'seed' | 'quality' | 'still'>
) {
  const data = await loadData(screens);
  if (!data) return null;
  const citizens = await loadCitizens(citizensUrl);
  if (!citizens.ok) {
    screens.errorModal.show('Data Error: citizens', [citizens.error]);
    return null;
  }

  const stack = createRenderStack(data, options.quality);
  const reporter = createProgressReporter(
    getTotalBuildWeight(),
    (fraction, label) => {
      screens.loadingScreen.setProgress(buildBarFraction(fraction), label);
    },
    createPaintYield(win)
  );

  let world: Awaited<ReturnType<typeof buildWorld>>;
  try {
    world = await buildWorld(
      { data, seed: options.seed, materials: stack.materials, citizens: citizens.value },
      reporter
    );
    stack.scene.add(world.root);
    screens.loadingScreen.setProgress(COMPILE_START_FRACTION, 'Compiling shaders');
    await stack.pipeline.compile(stack.scene, stack.camera);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    screens.errorModal.show('World Build Error', [message]);
    return null;
  }

  const painterPose = poseFromCameraPose(data.painterView);
  const env: ControlEnv = {
    collision: world.collision,
    water: deferBoatWater(() =>
      buildBoatWater({
        site: world.site,
        palette: data.palette,
        landmarks: data.layout.landmarks,
        obstacles: world.obstacles,
        afloat: world.afloat,
      })
    ),
    swellAt: (p) => {
      const kind = world.site.queries.waterKindAt(p);
      if (!kind) return 0;
      return waveHeight(p, isOpenWater(WATER_KIND_CODE[kind]), sceneSeconds(options.still));
    },
    painterPose,
    spawn: world.spawn,
    groundHeightAt: (p) => world.site.queries.groundHeightAt(p),
  };
  return { ...stack, data, citizens: citizens.value, world, painterPose, env };
}

async function loadData({ loadingScreen, errorModal }: StartupScreens): Promise<AppData | null> {
  let download = createDownloadState(Object.keys(DATA_URLS).length);
  loadingScreen.setProgress(downloadFraction(download), 'Loading data...');
  const dataResult = await loadAppData(DATA_URLS, (p) => {
    download = advanceDownload(download, p);
    loadingScreen.setProgress(downloadFraction(download), `Loading ${p.file}...`);
  });
  if (!dataResult.ok) {
    const e = dataResult.error;
    errorModal.show(`Data Error: ${e.file}`, [e.message, ...e.details]);
    return null;
  }
  return dataResult.value;
}

function createRenderStack(data: AppData, qualityTier: QualityTier) {
  const renderer = createRenderer();
  const scene = new Scene();
  const camera = new PerspectiveCamera(CAMERA.defaultFovYDeg, 1, 0.1, WORLD.cameraFar);
  const quality = QUALITY_TIERS[qualityTier];

  const style = createStyleParams(data.palette, data.painterView);
  const materials = createMaterialFactory(data.palette, style);
  const pipeline = createPostPipeline(renderer, style, quality, materials.glow());
  handleResize(renderer, camera, pipeline, materials, quality);

  const skyMesh = createSkyDome(materials);
  skyMesh.name = 'sky';
  scene.add(skyMesh);
  return { renderer, scene, camera, materials, pipeline };
}

/* Paint progress between chunks once a frame's worth of work has run. Hidden tabs yield tasks
   instead of waiting for suspended vsync; visible tabs also have a bounded fallback. */
function createPaintYield(win: Window): () => Promise<void> {
  let lastPaintMs = Number.NEGATIVE_INFINITY;
  return async () => {
    if (!isPaintDue(win.performance.now(), lastPaintMs)) return;
    lastPaintMs = await afterNextPaint(win);
  };
}
